import { useEffect, useState } from 'react'
import { Scale, Loader2, Plus, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api, getApiErrorMessage } from '@/lib/api'
import { toast } from 'sonner'

interface LegalHold {
  id: number
  account_id: string
  reason: string
  placed_by: string
  placed_at: string
  expires_at: string | null
  lifted_at: string | null
  lifted_by: string | null
}

// CHECKPOINT: PRD-09 FR-9.12.4 — Legal hold management UI
export function LegalHoldsTab() {
  const [holds, setHolds] = useState<LegalHold[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [accountId, setAccountId] = useState('')
  const [reason, setReason] = useState('')

  const fetchHolds = async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/security/legal-holds')
      setHolds(res.data.holds || [])
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load legal holds'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchHolds()
  }, [])

  const handlePlace = async () => {
    if (!accountId || !reason) {
      toast.error('Account ID and reason are required')
      return
    }
    setSubmitting(true)
    try {
      await api.post('/admin/security/legal-hold', { account_id: accountId, reason })
      toast.success('Legal hold placed')
      setDialogOpen(false)
      setAccountId('')
      setReason('')
      fetchHolds()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to place legal hold'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleLift = async (accountId: string) => {
    if (!confirm('Lift this legal hold? The account will become eligible for purge again.')) return
    try {
      await api.delete(`/admin/security/legal-hold/${accountId}`)
      toast.success('Legal hold lifted')
      fetchHolds()
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to lift legal hold'))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setDialogOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Place Legal Hold
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Scale className="h-4 w-4" />
            Legal Holds
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Account</th>
                  <th className="px-4 py-3 text-left font-medium">Reason</th>
                  <th className="px-4 py-3 text-left font-medium">Placed By</th>
                  <th className="px-4 py-3 text-left font-medium">Placed At</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : holds.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No legal holds
                    </td>
                  </tr>
                ) : (
                  holds.map((h) => (
                    <tr key={h.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-muted-foreground">{h.account_id.slice(0, 8)}...</td>
                      <td className="px-4 py-3 max-w-xs truncate">{h.reason}</td>
                      <td className="px-4 py-3 text-muted-foreground">{h.placed_by.slice(0, 8)}...</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(h.placed_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        {h.lifted_at ? (
                          <span className="text-emerald-500">Lifted</span>
                        ) : (
                          <span className="text-amber-500">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!h.lifted_at && (
                          <Button variant="ghost" size="icon" onClick={() => handleLift(h.account_id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Place Legal Hold</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium">Account ID</label>
              <Input value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="uuid" />
            </div>
            <div>
              <label className="text-sm font-medium">Reason</label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this account under legal hold?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePlace} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Place Hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
